/*
 * Layout.tsx
 * Components for the layout of the application.
 */
import { ApplicationContext } from '../App';
import PendantButton, { TextButton } from './PendantButton';
import { TabOption } from './Sidebar';
import { wrapChangeEventString, COLOR } from './shared/shared';
import { Application } from 'libapi';
import {
	ReactElement,
	useState,
	useEffect,
	useContext,
	createContext,
	useRef,
	useMemo,
} from 'react';
import SimpleKeyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';

function MySimpleKeyboard(pProps: any) {
	/* Hack for strange esbuild behavior */
	if (typeof SimpleKeyboard === 'function') {
		return <SimpleKeyboard {...pProps} />;
	}
	const lDefault = (SimpleKeyboard as any).default;
	return lDefault(pProps);
}

export interface ContentSplitProps {
	children: ReactElement; // sidebar
	pContent: ReactElement; // main
	className?: string;
	hideAside?: boolean;
}

export function ContentSplitFlip({
	children,
	pContent,
	className,
}: ContentSplitProps) {
	return (
		<div className={'full-height full-width ' + (className || 'grid-col-86')}>
			<article className="full-height full-width flex-center">{pContent}</article>
			<aside className="full-height full-width flex-center">{children}</aside>
		</div>
	);
}

export function ContentSplit({
	children,
	pContent,
	className,
	hideAside,
}: ContentSplitProps) {
	return (
		<div className={'full-height full-width ' + (className || 'grid-col-16')}>
			{(hideAside === undefined || !hideAside) && (
				<aside className="full-height full-width flex-center">{children}</aside>
			)}
			<article className="full-height full-width flex-center">{pContent}</article>
		</div>
	);
}

export interface LayoutContentProps {
	pTab: TabOption;
	children: ReactElement;
	dialog?: boolean;
	popup?: boolean;
	modal?: boolean;
	hideHeader?: boolean;
}

type KeyboardSetFn = (pNewText: string) => void;
export type KeyboardTrigger = (
	pPromptText: string,
	pCurrentValue: string,
	pHandleSet: KeyboardSetFn,
	inputType?: string
) => void;

type SelectSetFn = (pNewIndex: number) => void;
export type SelectTrigger = (
	pPromptText: string,
	pOptions: string[],
	pHandleSelect: SelectSetFn,
	pCurrentIndex?: number
) => void;

export const KeyboardContext = createContext<KeyboardTrigger>(
	(
		pPromptText: string,
		pCurrentValue: string,
		pHandleSet: KeyboardSetFn,
		inputType?: string
	) => {
		console.log('Keyboard Context Not Set');
		return;
	}
);
export const SelectContext = createContext<SelectTrigger>(
	(pPromptText: string, pOptions: string[], pHandleSelect: SelectSetFn) => {
		console.log('Select Context Not Set.');
		return;
	}
);

export function LayoutContent({
	pTab,
	children,
	dialog,
	popup,
	modal,
	hideHeader,
}: LayoutContentProps) {
	const [lShowKeyboard, lSetShowKeyboard] = useState<boolean>(false),
		[lShowSelect, lSetShowSelect] = useState<boolean>(false),
		lApp = useContext(ApplicationContext) as Application,
		[lKeyboardProps, lSetKeyboardProps] = useState<KeyboardProps>({
			pPromptText: 'Default Prompt Text',
			pCurrentValue: '',
			pHandleSet: (pNewString: string) => {
				console.log('pHandleSet default:', pNewString);
				return;
			},
			pCloseKeyboard: () => lSetShowKeyboard(false),
		}),
		lTriggerKeyboard: KeyboardTrigger = function (
			pPromptText: string,
			pCurrentValue: string,
			pHandleSet: KeyboardSetFn,
			inputType?: string
		) {
			// if (!lApp.api.isWebDeployment()) {
				// Note: no on-screen keyboard in the web flavor.
				// Keeping same input field modals for web deployment
				const lCp = { ...lKeyboardProps };
				lCp.pPromptText = pPromptText;
				lCp.pHandleSet = pHandleSet;
				lCp.pCurrentValue = pCurrentValue;
				lCp.inputType = inputType;
				lSetKeyboardProps(lCp);
				lSetShowSelect(false);
				lSetShowKeyboard(true);
			// }
		},
		[lSelectProps, lSetSelectProps] = useState<SelectBrowserProps>({
			pPromptText: 'Default Prompt Text',
			pHandleSelect: function (pIndex) {
				return;
			},
			pHandleClose: function () {
				lSetShowSelect(false);
			},
			pOptions: [],
			pSelectedIndex: void 0,
		}),
		lTriggerSelect: SelectTrigger = function (
			pPromptText: string,
			pOptions: string[],
			pHandleSelect: SelectSetFn,
			pCurrentIndex?: number
		) {
			const lCp = { ...lSelectProps };
			lCp.pOptions = pOptions;
			lCp.pPromptText = pPromptText;
			lCp.pHandleSelect = pHandleSelect;
			lCp.pSelectedIndex = pCurrentIndex !== undefined ? pCurrentIndex : void 0;
			lSetSelectProps(lCp);
			lSetShowKeyboard(false);
			lSetShowSelect(true);
		};

	return (
		<KeyboardContext.Provider value={lTriggerKeyboard}>
			<SelectContext.Provider value={lTriggerSelect}>
				{(function () {
					if (!!dialog) {
						return (
							<section className="LayoutDialogContainer vh-91 full-width">
								<article className="LayoutDialog">
									<main className="background-white height-95 width-85 flex-center flex-col rounded">
										<div className="flex-center align-center">
											<span className="font-med-big bold-font color-blue">
												{pTab.Name}
											</span>
										</div>
										{children}
									</main>
									{lShowKeyboard && <Keyboard {...lKeyboardProps} />}
									{lShowSelect && <SelectBrowser {...lSelectProps} />}
								</article>
							</section>
						);
					} else if (!!popup) {
						return (
							<section className="LayoutDialogContainer vh-91 full-width">
								<article className="LayoutDialog">
									<main className="background-white height-45 width-45 flex-center flex-col rounded">
										<div className="flex-center align-center">
											<span className="font-med-big bold-font color-blue">
												{pTab.Name}
											</span>
										</div>
										{children}
									</main>
									{lShowKeyboard && <Keyboard {...lKeyboardProps} />}
									{lShowSelect && <SelectBrowser {...lSelectProps} />}
								</article>
							</section>
						);
					} else if (!!modal) {
						return (
							<section className="LayoutDialogContainer full-height full-width">
								<article className="LayoutDialog">
									<main className="background-white height-40 width-30 flex-center flex-col rounded">
										<div className="flex-center align-center">
											<span className="font-med-big bold-font color-blue">
												{pTab.Name}
											</span>
										</div>
										{children}
									</main>
									{lShowKeyboard && <Keyboard {...lKeyboardProps} />}
									{lShowSelect && <SelectBrowser {...lSelectProps} />}
								</article>
							</section>
						);
					} else
						return (
							<section className="full-height full-width">
								<article className="LayoutContent">
									{!hideHeader && (
										<header className="full-width flex-row flex-between align-center padding-right padding-top padding-bottom">
											<h4 className="padding">
												<span
													className={pTab.IconClass + ' color-blue font-large'}
													style={{ fontWeight: 700 }}
												></span>
												<b className="padding-left">{pTab.Name}</b>
											</h4>
										</header>
									)}
									<main className="full-height full-width flex-center">{children}</main>
									{lShowKeyboard && <Keyboard {...lKeyboardProps} />}
									{lShowSelect && <SelectBrowser {...lSelectProps} />}
								</article>
							</section>
						);
				})()}
			</SelectContext.Provider>
		</KeyboardContext.Provider>
	);
}

interface SelectBrowserProps {
	pOptions: string[];
	pHandleSelect(pValue: number): void;
	pHandleClose(): void;
	pPromptText: string;
	pSelectedIndex?: number;
}

function SelectBrowser({
	pOptions,
	pHandleClose,
	pHandleSelect,
	pPromptText,
	pSelectedIndex,
}: SelectBrowserProps) {
	const [lSearchText, lSetSearchText] = useState<string>(''),
		lKeyboardProps = useKeyboard(lSetSearchText),
		[lSelectedIndex, lSetSelectedIndex] = useState<number | undefined>(
			pSelectedIndex !== void 0 && pSelectedIndex < pOptions.length
				? pSelectedIndex
				: void 0
		),
		lHandleSelect = function () {
			if (lSelectedIndex === undefined) return;
			pHandleSelect(lSelectedIndex);
			pHandleClose();
		},
		lSearchRegexp = useMemo(
			function () {
				if (lSearchText.length === 0) return void 0;
				return new RegExp(lSearchText, 'i');
			},
			[lSearchText]
		),
		lItemLimit = 5,
		[lOffset, lSetOffset] = useState<number>(0),
		lAvailableOptions = useMemo(
			function () {
				const lSearchRegexp =
					lSearchText.length === 0 ? void 0 : new RegExp(lSearchText, 'i');
				return pOptions
					.map(function (pOption, pIndex) {
						return [pOption, pIndex] as [string, number];
					})
					.filter(function (pOptionPair) {
						if (!lSearchRegexp) return true;
						return lSearchRegexp.test(pOptionPair[0]);
					});
			},
			[pOptions, lOffset, lSearchText]
		),
		lMaxPages = useMemo(
			function () {
				return Math.ceil(lAvailableOptions.length / lItemLimit);
			},
			[lAvailableOptions]
		),
		lDisplayString = `${lOffset + 1} of ${lMaxPages}`;
	useEffect(
		function () {
			if (lMaxPages - 1 <= lOffset) lSetOffset(0);
		},
		[lMaxPages]
	);

	return (
		<div className="Keyboard">
			<div className="full-height full-width flex-center">
				<div className="height-96 width-50 background-off-white container-border grid-row-86">
					<div className="full-size flex-col flex-start align-center">
						<h4 className="padding"> {pPromptText} </h4>
						<input
							className="padding width-40"
							type="text"
							placeholder="Search"
							value={lSearchText}
							onChange={wrapChangeEventString(lSetSearchText)}
						/>
						{pOptions.length > lItemLimit && (
							<div className="full-width flex-center">
								<div className="width-40 flex-around">
									<div className="full-height width-auto flex-start justify-center cursor-pointer">
										<TextButton
											pSpanClass="icon-arrow-left half-padding-top large-font"
											pText=""
											pAction={function () {
												lSetOffset(Math.max(0, lOffset - 1));
											}}
											pClickColor={COLOR.GREEN}
											pDefaultColor={COLOR.DARK_BLUE}
										/>
									</div>
									<div className="full-height width-auto flex-start justify-center cursor-pointer">
										<h5 className="padding-top-10"> {lDisplayString} </h5>
									</div>
									<div className="full-height width-auto flex-start justify-center cursor-pointer">
										<TextButton
											pSpanClass="icon-arrow-right half-padding-top large-font"
											pText=""
											pAction={function () {
												lSetOffset(Math.min(lMaxPages - 1, lOffset + 1));
											}}
											pClickColor={COLOR.GREEN}
											pDefaultColor={COLOR.DARK_BLUE}
										/>
									</div>
								</div>
							</div>
						)}
						<div className="full-size flex-center flex-col flex-start align-center">
							{pOptions
								.map(function (pOption: string, pIndex: number) {
									return [pOption, pIndex] as [string, number];
								})
								.filter(function (pPair) {
									if (lSearchRegexp && !lSearchRegexp.test(pPair[0])) return false;
									return true;
								})
								.map(function (pPair: [string, number], pIndex: number) {
									const [lOption, lIndex] = pPair;
									if (pIndex >= (lOffset + 1) * lItemLimit) return;
									if (pIndex < lOffset * lItemLimit) return;
									const lClass =
											lIndex === lSelectedIndex
												? 'background-medium-blue border-medium-blue color-white'
												: 'container-border background-desert-storm',
										lHandleClick = function () {
											lSetSelectedIndex(lIndex);
										};
									return (
										<div
											key={pIndex}
											className={
												'flex-center cursor-pointer min-width-40 margin-top padding rounded ' +
												lClass
											}
											onClick={lHandleClick}
										>
											<h4> {lOption} </h4>
										</div>
									);
								})}
						</div>
					</div>
					<div className="full-size align-center flex-around">
						<PendantButton pClass="width-26" pAction={pHandleClose} pClickRed={true}>
							<span> Cancel </span>
						</PendantButton>
						<PendantButton
							pClass="width-26"
							pAction={lHandleSelect}
							pDisabled={lSelectedIndex === undefined}
						>
							<span> Done </span>
						</PendantButton>
					</div>
				</div>
			</div>
			<div className="KeyboardContainer full-height full-width flex-center">
				<MySimpleKeyboard {...lKeyboardProps} />
			</div>
		</div>
	);
}

interface KeyboardProps {
	pPromptText: string;
	pCurrentValue: string;
	pHandleSet(pNewString: string): void;
	pCloseKeyboard(): void;
	inputType?: string;
}

enum KeyboardMode {
	Default = 'default',
	Shift = 'shift',
	Caps = 'caps',
}

const useKeyboard = function (pOnChange: (pString: string) => void) {
	const [mode, setMode] = useState<KeyboardMode>(KeyboardMode.Default);

	const lOnKeyPress = (button: string) => {
		if (button === '{shift}') {
			if (mode === KeyboardMode.Shift) {
				setMode(KeyboardMode.Default);
			} else setMode(KeyboardMode.Shift);
		} else if (button === '{lock}') {
			if (mode === KeyboardMode.Caps) {
				setMode(KeyboardMode.Default);
			} else setMode(KeyboardMode.Caps);
		} else {
			if (mode === KeyboardMode.Shift) {
				setMode(KeyboardMode.Default);
			}
		}
	};

	return {
		onKeyPress: lOnKeyPress,
		layoutName: mode === KeyboardMode.Caps ? KeyboardMode.Shift : mode,
		onChange: pOnChange,
	};
};

function Keyboard({
	pPromptText,
	pCurrentValue,
	pHandleSet,
	pCloseKeyboard,
	inputType = 'text',
}: KeyboardProps) {
	const [value, setValue] = useState<string>(pCurrentValue);
	const kbRef = useRef<any>(null);
	const syncKeyboard = (val: string) => {
		setValue(val);
		kbRef.current?.setInput(val); // update r‑s‑k internal value
	};

	const keyboardProps = useMemo(
		() => ({
			...useKeyboard(syncKeyboard), // supplies onChange & onKeyPress
			keyboardRef: (r: any) => (kbRef.current = r),
			// pre‑populate the keyboard the moment it is ready
			onInit: (k: any) => k.setInput(pCurrentValue),
		}),
		[syncKeyboard, pCurrentValue]
	);

	const inputRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		const el = inputRef.current;
		if (!el) return;
		el.focus();
		const len = el.value.length;
		el.setSelectionRange(len, len);
	}, []);

	const handleCancel = () => pCloseKeyboard();
	const handleSubmit = () => {
		pHandleSet(value);
		pCloseKeyboard();
	};

	return (
		<div className="Keyboard">
			<div className="full-height full-width flex-center">
				<div className="height-50 width-40 grid-row-20-40 rounded background-off-white">
					<div className="full-height full-width flex-center font-large">
						<h4>{pPromptText}</h4>
					</div>
					<div className="full-height full-width flex-center font-large flex-col">
						<input
							ref={inputRef}
							className="width-70"
							type={inputType}
							value={value}
							placeholder={pPromptText}
							onChange={e => syncKeyboard(e.target.value)}
						/>
					</div>
					<div className="full-height full-width flex-center font-large">
						<div className="width-70 full-height flex-between align-center">
							<PendantButton
								pAction={handleCancel}
								pClickRed
								pClass="color-white flex-center rounded background-medium-blue height-44 width-40"
							>
								<span>Cancel</span>
							</PendantButton>
							<PendantButton
								pAction={handleSubmit}
								pDisabled={value === pCurrentValue || value.length === 0}
								pClass="color-white flex-center rounded background-medium-blue height-44 width-40"
							>
								<span>Submit</span>
							</PendantButton>
						</div>
					</div>
				</div>
			</div>

			{/* on‑screen keyboard  */}
			<div className="KeyboardContainer full-height full-width flex-center">
				<MySimpleKeyboard {...keyboardProps} />
			</div>
		</div>
	);
}

export function Loading({ className, ...props }: any) {
	return (
		<div className={'loading-spinner ' + (className || '')} {...props}>
			<div></div>
			<div></div>
			<div></div>
			<div></div>
			<div></div>
			<div></div>
			<div></div>
			<div></div>
			<div></div>
			<div></div>
			<div></div>
			<div></div>
		</div>
	);
}
